import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Package, PlusCircle, FilterIcon } from 'lucide-react';
import AppliedFilters from '@/components/AppliedFilters';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import DataTable from '@/components/DataTable';
import DynamicFormModal from '@/components/DynamicFormModal';
import FilterDrawer from '@/components/FilterDrawer';
import ExportExcelButton from '@/components/ExportExcelButton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { consumablesColumns } from './utils/consumablesColumns';
import { createConsumable, getConsumables, updateConsumable, deleteConsumable, exportConsumables } from './Services/consumables.services';
import { fetchProviders } from '@/pages/parametrizacion/providers/Services/providers.services';

const ConsumablesPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ query: {}, page: 1, limit: 10 });
  const [totalRecords, setTotalRecords] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [providers, setProviders] = useState([]);
  const { toast } = useToast();


  const transformFiltersForQuery = useCallback((query) => {
    const transformedQuery = { ...query };
    return transformedQuery;
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Transformar filtros antes de enviar
      const transformedQuery = transformFiltersForQuery(filters.query);
      const response = await getConsumables({ ...filters, query: transformedQuery });
      
      // Asegurar que siempre sea un array
      setData(Array.isArray(response.data) ? response.data : []);
      setTotalRecords(response.totalRecords || 0);
    } catch (error) {
      console.error('Error al cargar consumibles:', error);
      setData([]);
      setTotalRecords(0);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los consumibles.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [filters, toast, transformFiltersForQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const loadProviders = async () => {
      try {
        const response = await fetchProviders({ limit: 1000, offset: 1, query: { estado: 'Activo' } });
        setProviders(response.data.map(provider => ({
          value: provider._id,
          label: provider.nombre
        })));
      } catch (error) {
        console.error('Error loading providers:', error);
      }
    };
    loadProviders();
  }, []);

  const handleFilter = (newFilters) => {
    setFilters((prev) => ({ ...prev, query: newFilters, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setFilters((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleSave = async (formData) => {
    try {
      // Transform proveedor if it's just an ID to the expected format
      const transformedData = { ...formData };
      if (transformedData.proveedor && typeof transformedData.proveedor === 'string') {
        const provider = providers.find(p => p.value === transformedData.proveedor);
        if (provider) {
          transformedData.proveedor = {
            id: provider.value,
            nombre: provider.label
          };
        }
      }
      
      if (editingItem) {
        const response = await updateConsumable(editingItem._id, transformedData);
        if (response?.state) {
          toast({ title: 'Éxito', description: 'Consumible actualizado correctamente.' });
        } else {
          toast({ title: 'Error', description: response.message, variant: 'destructive' });
        }
      } else {
        const response = await createConsumable(transformedData);
        if (response?.state) {
          toast({ title: 'Éxito', description: 'Consumible creado correctamente.' });
        } else {
          toast({ title: 'Error', description: response.message, variant: 'destructive' });
        }
      }
      fetchData();
      setIsModalOpen(false);
      setEditingItem(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: `No se pudo guardar el consumible. ${error.message}`,
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      try {
        await deleteConsumable(itemToDelete._id);
        toast({ title: 'Éxito', description: 'Consumible eliminado correctamente.' });
        fetchData();
      } catch (error) {
        toast({
          title: 'Error',
          description: 'No se pudo eliminar el consumible.',
          variant: 'destructive',
        });
      }
    }
    setIsDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleAction = (action, item) => {
    if (action === 'edit') {
      handleEdit(item);
    } else if (action === 'delete') {
      handleDelete(item);
    }
  };

  const tableColumns = consumablesColumns;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-primary/10 p-3 rounded-lg">
              <Package className="h-8 w-8 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl">Parametrización de insumos</CardTitle>
              <CardDescription>Gestiona los insumos del sistema.</CardDescription>
            </div>
          </div>
          <div className="flex gap-2 mt-4 md:mt-0">
            <Button onClick={() => setIsFilterOpen(true)} variant="outline">
              <FilterIcon className="h-4 w-4 mr-2" />
              Filtrar
            </Button>
            <ExportExcelButton
              service={(params) => exportConsumables(params.query)}
              filters={filters.query}
              columns={[
                { key: 'nombre', header: 'Nombre' },
                { key: 'descripcion', header: 'Descripción' },
                { key: 'proveedor.nombre', header: 'Proveedor' },
                { key: 'precio', header: 'Precio' },
                { key: 'tipo', header: 'Tipo' },
                { key: 'estado', header: 'Estado' },
              ]}
              fileName="Insumos"
            />
            <Button onClick={() => {
              setEditingItem(null);
              setIsModalOpen(true);
            }}>
              <PlusCircle className="h-4 w-4 mr-2" />
              Crear Nuevo
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <AppliedFilters 
            filters={filters.query} 
            onRemoveFilter={(key) => {
              const newQuery = { ...filters.query };
              delete newQuery[key];
              setFilters(prev => ({ ...prev, query: newQuery, page: 1 }));
            }}
            fields={[
              { id: 'nombre', label: 'Nombre', type: 'text' },
              { id: 'proveedor', label: 'Proveedor', type: 'select', options: providers },
              { id: 'tipo', label: 'Tipo', type: 'select', options: [
                { value: 'Limpieza', label: 'Limpieza' },
                { value: 'Amenities', label: 'Amenities' },
                { value: 'Mantenimiento', label: 'Mantenimiento' },
                { value: 'Cocina', label: 'Cocina' },
                { value: 'Baño', label: 'Baño' },
                { value: 'Oficina', label: 'Oficina' },
                { value: 'Otro', label: 'Otro' },
              ]},
              { id: 'estado', label: 'Estado', type: 'select', options: [
                { value: 'Activo', label: 'Activo' },
                { value: 'Inactivo', label: 'Inactivo' },
              ]},
            ]}
          />
          <DataTable
            data={data}
            columns={tableColumns}
            isLoading={loading}
            onAction={handleAction}
            page={filters.page}
            limit={filters.limit}
            totalRecords={totalRecords}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            columnsConfig={consumablesColumns}
          />
        </CardContent>
      </Card>

      {isModalOpen && (
        <DynamicFormModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSave}
          fields={consumablesColumns.map(field => {
            // Pass pre-loaded providers to the proveedor field
            if (field.id === 'proveedor') {
              return {
                ...field,
                type: 'select',
                options: providers
              };
            }
            return field;
          })}
          item={editingItem}
          title={editingItem ? 'Editar Consumible' : 'Crear Consumible'}
        />
      )}

      <FilterDrawer
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApplyFilters={handleFilter}
        fields={[
          { id: 'nombre', label: 'Nombre', type: 'text' },
          { id: 'proveedor', label: 'Proveedor', type: 'select', options: providers },
          { id: 'tipo', label: 'Tipo', type: 'select', options: [
            { value: 'Limpieza', label: 'Limpieza' },
            { value: 'Amenities', label: 'Amenities' },
            { value: 'Mantenimiento', label: 'Mantenimiento' },
            { value: 'Cocina', label: 'Cocina' },
            { value: 'Baño', label: 'Baño' },
            { value: 'Oficina', label: 'Oficina' },
            { value: 'Otro', label: 'Otro' },
          ]},
          { id: 'estado', label: 'Estado', type: 'select', options: [
            { value: 'Activo', label: 'Activo' },
            { value: 'Inactivo', label: 'Inactivo' },
          ]},
        ]}
        initialFilters={filters.query}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente el consumible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

export default ConsumablesPage;
